import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { orderBy } from 'firebase/firestore';
import type { DocumentVersion } from '@allemnii-pulse/shared';
import { useCollection } from '../../lib/firestore.js';
import { useProject } from '../projects/ProjectProvider.js';
import { diffObjects, type FieldDiff } from '../../lib/diff.js';
import { diffText } from '../../lib/textDiff.js';
import { api } from '../../lib/api.js';
import { Card } from '../../components/ui/Card.js';
import { Checkbox } from '../../components/ui/Checkbox.js';
import { Label } from '../../components/ui/Label.js';

function formatValue(v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (typeof v === 'string') return v;
  return JSON.stringify(v, null, 2);
}

/** Renders a word-level diff of two strings inline — added words highlighted green, removed
 * words struck through in red — instead of two separate full-text blocks the reader has to
 * compare by eye. */
function InlineTextDiff({ before, after }: { before: string; after: string }) {
  const parts = diffText(before, after);
  return (
    <pre className="whitespace-pre-wrap break-words rounded-lg border border-border bg-bg p-2 text-xs">
      {parts.map((part, i) => (
        <span
          key={i}
          className={
            part.added
              ? 'rounded-[3px] bg-[color-mix(in_srgb,var(--success)_25%,transparent)] text-success'
              : part.removed
                ? 'rounded-[3px] bg-[color-mix(in_srgb,var(--danger)_20%,transparent)] text-danger line-through'
                : undefined
          }
        >
          {part.value}
        </span>
      ))}
    </pre>
  );
}

function DiffRow({ diff }: { diff: FieldDiff }) {
  if (diff.status === 'unchanged') return null;
  const badgeClass =
    diff.status === 'added'
      ? 'bg-[color-mix(in_srgb,var(--success)_15%,transparent)] text-success'
      : diff.status === 'removed'
        ? 'bg-[color-mix(in_srgb,var(--danger)_15%,transparent)] text-danger'
        : 'bg-accent-soft text-accent-soft-fg';
  const isTextChange =
    diff.status === 'changed' && typeof diff.before === 'string' && typeof diff.after === 'string';

  return (
    <div className="flex flex-col gap-2 border-b border-border py-3 last:border-none">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 break-all font-mono text-xs font-semibold">{diff.path}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${badgeClass}`}
        >
          {diff.status}
        </span>
      </div>
      {isTextChange ? (
        <InlineTextDiff before={diff.before as string} after={diff.after as string} />
      ) : (
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <pre className="whitespace-pre-wrap break-words rounded-lg border border-border bg-bg p-2 text-xs">
            {formatValue(diff.before)}
          </pre>
          <pre className="whitespace-pre-wrap break-words rounded-lg border border-border bg-bg p-2 text-xs">
            {formatValue(diff.after)}
          </pre>
        </div>
      )}
    </div>
  );
}

export function VersionHistoryPage() {
  const { projectId } = useProject();
  const { docType } = useParams<{ docType: 'prd' | 'tdd' }>();
  const { data: versions } = useCollection<DocumentVersion>(
    `projects/${projectId}/documents/${docType}/versions`,
    [orderBy('versionNumber', 'desc')],
  );

  const [fromId, setFromId] = useState<string | null>(null);
  const [toId, setToId] = useState<string | null>(null);
  const [diffs, setDiffs] = useState<FieldDiff[] | null>(null);
  const [showUnchanged, setShowUnchanged] = useState(false);

  useEffect(() => {
    if (versions.length >= 2 && !fromId && !toId) {
      setFromId(versions[1]!.id);
      setToId(versions[0]!.id);
    }
  }, [versions, fromId, toId]);

  useEffect(() => {
    if (!fromId || !toId || !projectId) {
      setDiffs(null);
      return;
    }
    api
      .get<{ from: DocumentVersion; to: DocumentVersion }>(
        `/projects/${projectId}/documents/${docType}/diff?from=${fromId}&to=${toId}`,
      )
      .then((res) => setDiffs(diffObjects(res.from.content, res.to.content)))
      .catch(() => setDiffs(null));
  }, [fromId, toId, projectId, docType]);

  const changedCount = diffs?.filter((d) => d.status !== 'unchanged').length ?? 0;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-lg font-semibold uppercase">{docType} version history</h1>
          <p className="text-sm text-muted">Versions are immutable — nothing here can be edited.</p>
        </div>
        <Link
          to={`/projects/${projectId}/${docType}`}
          className="-my-2 ml-auto py-2 text-xs text-muted hover:text-text"
        >
          ← Back
        </Link>
      </div>

      <div className="flex flex-col gap-4 md:flex-row">
        {/* Stacked above the diff on a phone, so a long history scrolls inside its own box
            instead of pushing the comparison off-screen. */}
        <Card className="flex max-h-64 w-full flex-col divide-y divide-border overflow-y-auto md:max-h-none md:w-56 md:flex-shrink-0 md:overflow-visible">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center gap-2 p-2 text-xs">
              <span className="font-mono">v{v.versionNumber}</span>
              <span className="flex-1 truncate text-muted">
                {v.acceptedAt ? 'accepted' : v.source}
              </span>
              <button
                onClick={() => setFromId(v.id)}
                className={`flex h-9 min-w-9 items-center justify-center rounded px-1.5 md:h-auto md:min-w-0 md:py-0.5 ${fromId === v.id ? 'bg-danger/20 text-danger' : 'hover:bg-surface-2'}`}
              >
                A
              </button>
              <button
                onClick={() => setToId(v.id)}
                className={`flex h-9 min-w-9 items-center justify-center rounded px-1.5 md:h-auto md:min-w-0 md:py-0.5 ${toId === v.id ? 'bg-success/20 text-success' : 'hover:bg-surface-2'}`}
              >
                B
              </button>
            </div>
          ))}
        </Card>

        <Card className="min-w-0 flex-1 p-4">
          {!diffs ? (
            <p className="text-sm text-muted">Pick an "A" and a "B" version to compare.</p>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{changedCount} fields changed</span>
                <Label
                  htmlFor="show-unchanged"
                  className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted"
                >
                  <Checkbox
                    id="show-unchanged"
                    checked={showUnchanged}
                    onCheckedChange={(c) => setShowUnchanged(c === true)}
                  />
                  Show unchanged
                </Label>
              </div>
              <div className="flex flex-col">
                {diffs
                  .filter((d) => showUnchanged || d.status !== 'unchanged')
                  .map((d) => (
                    <DiffRow key={d.path} diff={d} />
                  ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
